/**
 * ── The handler, and the filter read through it ──────────────────────────────
 *
 * A handler is the dispatcher dealing with a transport — the person a colleague
 * hands the phone to. It sits on an intake order, on a container order, and on
 * each container under it, and it exists for one reason: an office of twelve
 * dispatchers sharing four screens cannot tell whose work is whose.
 *
 * It is a **view preference and never a permission.** Nothing reads a handler to
 * decide what anybody may do; every action stays open to every dispatcher,
 * reassignment included. The only thing it narrows is a list somebody chose to
 * narrow, and clearing the filter always shows everything.
 */

/**
 * A handler as every screen receives one — enough for a bubble and a tooltip.
 *
 * The names are optional because the records that carry them are older than
 * this field: a user invited before first and last name were both required has
 * one of them, and the intake queue's own user refs have always been typed this
 * way. `handlerInitials` and `handlerName` both cope, falling back to the email
 * and then to nothing, so a bubble degrades to a dot rather than to "undefined".
 */
export interface Handler {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/**
 * The same person on the planning board, which flattens its refs.
 *
 * The board is a view model nothing posts back, and a week of every truck can
 * carry two hundred of these — so `id` rather than `_id`, and no email. The
 * names may be empty: the board's mutation endpoints answer with a card built
 * inside a transaction and do not pay for a lookup to fill them in, so the page
 * resolves the id against the dispatcher list it already holds for the filter.
 */
export interface BoardHandler {
  id: string;
  firstName?: string;
  lastName?: string;
}

/**
 * What the filter is currently set to.
 *
 *   `all`          everyone — the default, and the state in which every screen
 *                  behaves exactly as it did before handlers existed.
 *   `mine`         the signed-in user. Resolved to their id before it is sent.
 *   `unassigned`   nobody has taken it — the backlog.
 *   a user id      that person's work.
 *
 * `mine` is a *control* state, not a wire value. Letting the server know it as a
 * word would be a second spelling of a question it can already answer, and two
 * spellings is how the orders list and the board end up disagreeing about what
 * "mine" means. See `handlerScopeParam`.
 */
export type HandlerScope = "all" | "mine" | "unassigned" | (string & {});

/** The default every screen opens on. See `handlerScopeParam` for why. */
export const DEFAULT_HANDLER_SCOPE: HandlerScope = "all";

/**
 * The scope value meaning "nobody has taken this".
 *
 * A real value on the wire, unlike `mine` — the server has to be able to tell
 * "show me the backlog" from "show me everything", and it cannot infer either
 * from an absent parameter. Exported because anything comparing a *resolved*
 * param against a handler id has to special-case it; see `matchesHandlerParam`,
 * which exists precisely so nobody writes that comparison by hand again.
 */
export const UNASSIGNED_HANDLER_SCOPE = "unassigned";

/**
 * The scope as the API takes it — `undefined` meaning "do not filter".
 *
 * **Why the default is Everyone and not Mine.** Every order and container that
 * exists today has no handler, so a screen that opened on Mine would open empty
 * for all twelve dispatchers on the morning this ships, and keep doing so until
 * somebody had claimed enough work to fill it. Everyone needs no backfill, no
 * bulk assignment and no migration: the data fills in as orders are worked, and
 * a dispatcher who switches to Mine stays there (see `useHandlerScope`).
 */
export const handlerScopeParam = (
  scope: HandlerScope,
  meId: string | undefined,
): string | undefined => {
  if (scope === "all") return undefined;
  if (scope === "mine") return meId || undefined;
  // `unassigned` and a user id both travel as themselves.
  return scope || undefined;
};

/**
 * Does one record match the scope that was actually requested?
 *
 * Takes the **resolved param** — the same string that went to the server — and
 * not the control's state, so a screen deciding how to draw something can never
 * disagree with the set it was given. Deliberately not written inline at the
 * call site: the obvious one-liner,
 *
 *     (card.handler?.id ?? null) === handlerParam
 *
 * is right for "mine" and for a named person and silently wrong for
 * `unassigned`, where the param is the literal word and never equals an id — so
 * *every* record read as out of scope, the unassigned ones included. That is
 * exactly the bug this replaced, on the planning grid, where it turned the
 * whole board into anonymous grey tiles the moment somebody asked to see the
 * backlog.
 *
 * Only the planning grid needs this. Every other screen narrows server-side and
 * draws whatever it is sent.
 */
export const matchesHandlerParam = (
  param: string | undefined,
  handlerId: string | null | undefined,
): boolean => {
  if (!param) return true;
  if (param === UNASSIGNED_HANDLER_SCOPE) return !handlerId;
  return handlerId === param;
};

/** Two initials, the way the list has always abbreviated a person. */
export const handlerInitials = (
  handler: Pick<Handler, "firstName" | "lastName"> | null | undefined,
): string =>
  handler
    ? `${handler.firstName?.[0] ?? ""}${handler.lastName?.[0] ?? ""}`
        .toUpperCase()
        .trim()
    : "";

/** The full name, for a tooltip and for the menu. */
export const handlerName = (
  handler: (Pick<Handler, "firstName" | "lastName"> & { email?: string }) | null | undefined,
): string =>
  handler
    ? `${handler.firstName ?? ""} ${handler.lastName ?? ""}`.trim() ||
      handler.email ||
      ""
    : "";
